import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { sendLoanRequestEmail } from "../lib/loanEmail";
import useInView from "../lib/useInView";
import couple from '../images/sky-high-couple.png';
import sky from '../images/choose-sky.png';
import "../styles/loans.css";
import "../styles/Home.css";

// Environment variables
const BRAND_NAME = process.env.REACT_APP_BRAND_NAME;

const words = [`Easy Loans`, "Flexibility", "Support"];

const Loan = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        amount: '',
        reason: '',
        duration: '',
        otherBanks: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const [currentWordIndex, setCurrentWordIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }, 4500);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                const docRef = doc(db, "users", currentUser.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setProfile(docSnap.data());
                }
            } else {
                setUser(null);
                setProfile(null);
            }
        });

        return () => unsubscribe();
    }, []);

    const handleApplyClick = () => {
        if (!user) {
            navigate('/login');
        } else {
            setShowModal(true);
        }
    };

    const handleFormChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleFormSubmit = async () => {
        if (!form.amount || !form.duration || !form.reason) {
            return;
        }

        setSubmitting(true);
        try {
            const loanDetails = {
                ...form,
                status: 'pending',
                date: new Date().toISOString()
            };

            // 1. Save to Firestore
            const userRef = doc(db, "users", user.uid);
            await setDoc(userRef, {
                loanDetails: loanDetails
            }, { merge: true });

            // 2. Send Email Notification to Admin
            await sendLoanRequestEmail({ ...profile, uid: user.uid }, loanDetails);

            window.location.reload();
        } catch (error) {
            console.error("Error submitting loan application:", error);
            setSubmitting(false);
        }
    };

    const [box1Ref, box1InView] = useInView({ threshold: 0.7 });

    return (
        <>
            {/* Hero Section */}
            <div id="hero-section">
                <div className="hero-section">
                    <div className="hero-text-container">
                        <h1 className="hero-title">
                            Empower Your Financial Future with <span className="handwriting">{words[currentWordIndex]}</span>
                        </h1>
                        <p className="hero-description">
                            Fast, transparent, and flexible loans designed to meet your unique needs. Whether it’s for a major purchase or unexpected expenses, we've got you covered.
                        </p>
                        <button 
                            className="delete-button" 
                            onClick={handleApplyClick}
                        >
                            {profile?.loanDetails ? "View Loan Status" : "Apply Now"}
                        </button>
                    </div>
                    <div className="hero-image-container" id="box-saving">
                        <img
                            src={sky}
                            alt="Loan Hero"
                            ref={box1Ref}
                            className={`hero-image animate__animated ${box1InView ? "custom-animation" : ""}`}
                        />
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay-loan" onClick={() => setShowModal(false)}>
                    <div className="modal-loan" onClick={(e) => e.stopPropagation()}>
                        {profile?.loanDetails ? (
                            <div className="loan-details-view">
                                <h2 className="modal-loan-title">Loan Details</h2>
                                <div>
                                    <p><span className="label">Amount:</span> {profile.loanDetails.amount}</p>
                                    <p><span className="label">Duration:</span> {profile.loanDetails.duration}</p>
                                    <p><span className="label">Reason:</span> {profile.loanDetails.reason}</p>
                                    <p><span className="label">Other Banks:</span> {profile.loanDetails.otherBanks || "None"}</p>
                                    <div className="card-status-row" style={{ marginTop: '15px' }}>
                                        <span className="card-status-label">Status:</span>
                                        <span className={`card-status-badge ${profile.loanDetails.status}`}>
                                            {profile.loanDetails.status.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div className="loan-actions">
                                    <button className="cancel-loan" onClick={() => setShowModal(false)}>Close</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h2 className="modal-loan-title">Loan Application</h2>

                                <label>Amount Needed</label>
                                <input
                                    className="input-field"
                                    type="text"
                                    placeholder="eg. 1000 USD"
                                    name="amount"
                                    value={form.amount}
                                    onChange={handleFormChange}
                                />

                                <label>Loan Duration</label>
                                <input
                                    className="input-field"
                                    placeholder="eg. 6 Months"
                                    type="text"
                                    name="duration"
                                    value={form.duration}
                                    onChange={handleFormChange}
                                />

                                <label>Reason for Loan</label>
                                <textarea
                                    className="input-field"
                                    placeholder="Please actually explain in details."
                                    name="reason"
                                    value={form.reason}
                                    onChange={handleFormChange}
                                />

                                <label>Other Banks Used (if any)</label>
                                <input
                                    className="input-field"
                                    type="text"
                                    placeholder="eg. Wells Fargo"
                                    name="otherBanks"
                                    value={form.otherBanks}
                                    onChange={handleFormChange}
                                />

                                <div className="loan-actions">
                                    <button 
                                        className="delete-button" 
                                        onClick={handleFormSubmit}
                                        disabled={submitting}
                                    >
                                        {submitting ? "Submitting..." : "Submit Application"}
                                    </button>
                                    <button className="cancel-loan" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Info Section */}
            <div className="hero-text-container">
                <h2 className="box-title">Our Loan Commitment</h2>
                <p className="hero-description">
                    Our loan system is designed to offer you financial flexibility when you need it the most. Whether you're planning a big purchase, dealing with unexpected expenses, or looking to consolidate your debts, we provide a streamlined process to get you the funds you need quickly and easily.
                </p>
                <div className="last-sections sub-containers">
                    <div className="sub-box">
                        <h2 id="white-text">Step 1: Application</h2>
                        <p className="box-text search-container">
                            Once we receive your application, our team will conduct a thorough review. We evaluate your creditworthiness and repayment capacity to tailor a loan package that suits your requirements.
                        </p>
                    </div>
                    <div className="sub-box">
                        <h2 id="white-text">Step 2: Evaluation</h2>
                        <p className="box-text search-container">
                            Upon approval, you'll receive a detailed loan agreement. It outlines the loan amount, interest rate, repayment schedule, and any fees involved.
                        </p>
                    </div>
                    <div className="sub-box">
                        <h2 id="white-text">Step 3: Repayment</h2>
                        <p className="box-text search-container">
                            We offer multiple repayment options to fit your financial situation. Our customer service team is always available to help you during the repayment period.
                        </p>
                    </div>
                </div>
            </div>

            {/* Visual Section */}
            <div className="video-section">
                <div className={`hero-image-container animate__animated ${box1InView ? "animate__zoomIn" : ""}`}>
                    <img src={couple} alt="Happy couple with loan" className="box-image" />
                </div>
                <div className="hero-text-container">
                    <h2 className="box-title">
                        Get Started Today and <span className="circle-text">Achieve More</span>
                    </h2>
                    <p className="hero-description">
                        Please note: Terms and conditions apply, including maintaining a minimum balance in your account to qualify for loans.
                    </p>
                    <button 
                        className="delete-button" 
                        onClick={handleApplyClick}
                    >
                        {profile?.loanDetails ? "View Loan Status" : "Apply for Loan"}
                    </button>
                </div>
            </div>

            {/* Features Section */}
            <div className="heading-cover-bg">
                <div className="last-section">
                    <div className="box">
                        <h2 className="text-heading centered" id="white-title">Why Choose {BRAND_NAME} Loans?</h2>
                        <div className="box-text" id="white-text">
                            Flexible terms, competitive interest rates, and personalized service for every borrower.
                        </div>
                        <div className="box-text">
                            <h2 className="text-heading" id="white-title">Responsible Lending</h2>
                            We are committed to responsible lending practices that prioritize your financial health and security.
                        </div>
                    </div>

                    <div className="box">
                        <h3 className="text-heading" id="white-title">Secure & Trusted</h3>
                        <div className="box-text">
                            Every loan transaction is processed through our encrypted, enterprise-grade banking systems — safeguarding your personal and financial data at every step.
                        </div>
                        <h3 className="text-heading" id="white-title">Transparent Terms</h3>
                        <div className="box-text">
                            We believe in complete clarity. All loan agreements include full disclosure of interest rates, repayment schedules, and fees — no surprises, just trust.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Loan;
